import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "hooks/useTitle";
import { Card, Row, Col, Form, Button } from "react-bootstrap";
import { ProgramsService } from "services/programs";
import { LookupService } from "services/lookupService";
import {
  DashboardFilterRequest,
  DashboardSummaryResult,
  MonthlyApplicationsChartResult,
  ApplicationsByCollegeChartResult,
  ApplicationsByRankAndGenderChartResult,
  ApplicationsByGenderChartResult,
  ApplicationsByDepartmentChartResult,
} from "services/programs/models/ProgramTypes";
import { CollegeResult } from "services/lookupService/models/LookupsType";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MultiSelectDropdown from "components/blocks/MultiSelectDropdown";
import Select from "react-select";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const COLORS = ["#667eea", "#f093fb", "#11998e", "#fa709a", "#ee0979", "#fbc7d4", "#4facfe", "#00f2fe"];

function OrdersStatisticsReportsView() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { t, i18n } = useTranslation(["Reports", "Shared"]);
  useTitle(t("Reports:ordersStatisticsReports"));


  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [selectedColleges, setSelectedColleges] = useState<number[]>([]);
  const [colleges, setColleges] = useState<CollegeResult[]>([]);

  const [summary, setSummary] = useState<DashboardSummaryResult | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyApplicationsChartResult[]>([]);
  const [collegeData, setCollegeData] = useState<ApplicationsByCollegeChartResult[]>([]);
  const [rankGenderData, setRankGenderData] = useState<ApplicationsByRankAndGenderChartResult[]>([]);
  const [genderData, setGenderData] = useState<ApplicationsByGenderChartResult[]>([]);
  const [departmentData, setDepartmentData] = useState<ApplicationsByDepartmentChartResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const y = currentYear - i;
    return { value: y, label: `${y}` };
  });

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "reports",
        path: "/home/reports",
        menuItemId: "reports",
      },
      {
        localizationKey: "ordersStatisticsReports",
        path: "/home/reports/ordersStatistics",
        menuItemId: "ordersStatisticsReports",
      },
    ]);

    loadColleges();
  }, [i18n.language]);

  useEffect(() => {
    loadDashboardData();
  }, [year, selectedColleges, i18n.language]);

  const loadColleges = async () => {
    try {
      const response = await LookupService.getAllCollegesDropDown();
      if (response?.data) {
        setColleges(response.data);
      }
    } catch (error) {
      console.error("Error loading colleges:", error);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const request: DashboardFilterRequest = {
        year,
        collegeIds: selectedColleges.length > 0 ? selectedColleges : undefined,
      };

      // Load all dashboard data
      const [summaryRes, monthlyRes, collegeRes, rankGenderRes, genderRes, departmentRes] = await Promise.all([
        ProgramsService.getDashboardSummary(request),
        ProgramsService.getMonthlyApplicationsChart(request),
        ProgramsService.getApplicationsByCollegeChart(request),
        ProgramsService.getApplicationsByRankAndGenderChart(request),
        ProgramsService.getApplicationsByGenderChart(request),
        ProgramsService.getApplicationsByDepartmentChart(request),
      ]);

      if (summaryRes?.data) setSummary(summaryRes.data);
      if (monthlyRes?.data) setMonthlyData(monthlyRes.data);
      if (collegeRes?.data) setCollegeData(collegeRes.data);
      if (rankGenderRes?.data) setRankGenderData(rankGenderRes.data);
      if (genderRes?.data) setGenderData(genderRes.data);
      if (departmentRes?.data) setDepartmentData(departmentRes.data);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalMonthName = (monthNumber: number): string => {
    const date = new Date(2000, monthNumber - 1, 1);
    return date.toLocaleString(i18n.language === "ar" ? "ar-SA" : "en-US", { month: "long" });
  };

  const localizedMonthlyData = monthlyData.map((item) => ({
    ...item,
    month: getLocalMonthName(item.monthNumber),
  }));

  const getColorForIndex = (index: number): string => {
    return COLORS[index % COLORS.length];
  };

  const handleExportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const pdfHeader = reportRef.current.querySelector(".pdf-header") as HTMLElement;
      if (pdfHeader) pdfHeader.style.display = "block";

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      if (pdfHeader) pdfHeader.style.display = "none";

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const orientation = imgWidth > imgHeight ? "l" : "p";
      const pdf = new jsPDF(orientation, "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const marginX = 10;
      const marginTop = 10;
      const marginBottom = 10;
      const usableWidth = pageW - marginX * 2;
      const usableHeight = pageH - marginTop - marginBottom;

      // Calculate scale to fit content on one page
      const widthRatio = usableWidth / (imgWidth * 0.264583); // Convert pixels to mm
      const heightRatio = usableHeight / (imgHeight * 0.264583);
      const scale = Math.min(widthRatio, heightRatio);

      const finalWidth = (imgWidth * 0.264583) * scale;
      const finalHeight = (imgHeight * 0.264583) * scale;

      // Center the content
      const xOffset = marginX + (usableWidth - finalWidth) / 2;
      const yOffset = marginTop + (usableHeight - finalHeight) / 2;

      pdf.addImage(imgData, "PNG", xOffset, yOffset, finalWidth, finalHeight);

      pdf.save(`${t("Reports:ordersStatisticsReports")}_${year}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Filter Section */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t("Reports:year")}</Form.Label>
                <Select
                  options={yearOptions}
                  value={yearOptions.find((o) => o.value === year)}
                  onChange={(selected) => selected && setYear(selected.value)}
                  placeholder={t("Common:select")}
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      height: "42px",
                    }),
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t("Reports:colleges")}</Form.Label>
                <MultiSelectDropdown
                  useReactSelect
                  data={colleges.map((c) => ({ id: c.id, value: c.collegeDetail?.displayName ?? "" }))}
                  value={selectedColleges}
                  onChange={(value) => {
                    setSelectedColleges(value.map((v) => Number(v)));
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Export PDF Button */}
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="primary"
          onClick={handleExportPdf}
          disabled={loading || exporting}
        >
          {exporting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t("Reports:exportAsPdf")}
            </>
          ) : (
            <>
              <i className="icon-download me-2"></i>
              {t("Reports:exportAsPdf")}
            </>
          )}
        </Button>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!loading && (
        <div ref={reportRef}>
        {/* PDF-only header */}
        <div className="pdf-header" style={{ display: "none" }}>
          <div style={{
            textAlign: "center",
            paddingBottom: 16,
            marginBottom: 16,
            borderBottom: "2px solid #2c3e50",
          }}>
            <h3 style={{ margin: 0, color: "#2c3e50", fontWeight: "bold" }}>
              {t("Reports:ordersStatisticsReports")}
            </h3>
            <p style={{ margin: "6px 0 0", color: "#666", fontSize: 14 }}>
              {t("Reports:year")}: {year} &nbsp;|&nbsp; {new Date().toLocaleDateString(i18n.language === "ar" ? "ar-SA" : "en-US")}
            </p>
          </div>
        </div>
        <>
          {/* Summary Statistics Section */}
          <div className="mb-4">
            <h4 className="mb-3 fw-bold" style={{ color: "#2c3e50" }}>
              {t("Reports:summaryStatistics")}
            </h4>
            {summary ? (
              <Row>
                <Col md={3} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 p-3 rounded">
                          <i className="icon-orders fs-3 text-primary"></i>
                        </div>
                        <div className="mx-2">
                          <p className="text-muted mb-1 small">{t("Reports:receivedApplications")}</p>
                          <h3 className="mb-0 fw-bold">{summary.receivedApplications}</h3>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="bg-success bg-opacity-10 p-3 rounded">
                          <i className="icon-check-list fs-3 text-success"></i>
                        </div>
                        <div className="mx-2">
                          <p className="text-muted mb-1 small">{t("Reports:acceptedApplications")}</p>
                          <h3 className="mb-0 fw-bold">{summary.acceptedApplications}</h3>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="bg-info bg-opacity-10 p-3 rounded">
                          <i className="icon-dashboard fs-3 text-info"></i>
                        </div>
                        <div className="mx-2">
                          <p className="text-muted mb-1 small">{t("Reports:acceptanceRate")}</p>
                          <h3 className="mb-0 fw-bold">{summary.acceptanceRate.toFixed(2)}%</h3>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="bg-warning bg-opacity-10 p-3 rounded">
                          <i className="icon-rewards fs-3 text-warning"></i>
                        </div>
                        <div className="mx-2">
                          <p className="text-muted mb-1 small">{t("Reports:totalFunding")}</p>
                          <h3 className="mb-0 fw-bold">{summary.totalFundingInMillions}M</h3>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ) : (
              <div className="text-center text-muted py-4">
                <p>{t("Common:noData")}</p>
              </div>
            )}
          </div>

          {/* Monthly Applications Chart */}
          <div className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h5 className="fw-bold mb-3">{t("Reports:monthlyApplications")}</h5>
                {monthlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={localizedMonthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#667eea" name={t("Reports:count")} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted py-4">
                    <p>{t("Common:noData")}</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* College and Gender Charts */}
          <Row>
            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="fw-bold mb-3">{t("Reports:applicationsByCollege")}</h5>
                  {collegeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={collegeData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis
                          dataKey="collegeName"
                          type="category"
                          width={150}
                          tick={{ textAnchor: i18n.language === "ar" ? "start" : "end" }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#11998e" name={t("Reports:count")} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>{t("Common:noData")}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="fw-bold mb-3">{t("Reports:applicationsByGender")}</h5>
                  {genderData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={genderData as any[]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry:any) => `${entry.gender}: ${entry.percentage.toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {genderData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getColorForIndex(index)} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>{t("Common:noData")}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Rank & Gender and Department Charts */}
          <Row>
            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="fw-bold mb-3">{t("Reports:applicationsByRankAndGender")}</h5>
                  {rankGenderData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={rankGenderData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="rank" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="maleCount" fill="#667eea" name={t("Reports:male")} />
                        <Bar dataKey="femaleCount" fill="#f093fb" name={t("Reports:female")} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>{t("Common:noData")}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="fw-bold mb-3">{t("Reports:applicationsByDepartment")}</h5>
                  {departmentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={departmentData as any[]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry:any) => entry.departmentName}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {departmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getColorForIndex(index)} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>{t("Common:noData")}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
        </div>
      )}
    </div>
  );
}

export default OrdersStatisticsReportsView;
